import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ORGANISATION_STATE, Organisation } from '../common/organisation';
import { OrganisationService } from './organisation.service';
import { USER_ROLE, getUserRoleName } from '../common/user';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../ui-components/dialogs/confirm-dialog.component';
import { UserService } from '../user/user.service';
import { FUNCTION, FunctionService } from '../model-services/function.service';
import { DEFAULT_URL } from '../app-urls';
import { AppComponent } from '../app.component';
import { Column } from '../ui-components/table/table-component';

const ACCEPT_INVITE = "Accept invite"

@Component({
    selector: 'app-organisations',
    templateUrl: './organisations.component.html',
    styleUrls: ['./organisations.component.css'],
    standalone: false
})
export class OrganisationsComponent implements OnChanges {
    @Input() organisations:any[]

    // asks the page to reload the list after an organisation is changed, closed or deleted
    @Output() changed = new EventEmitter<void>()

    organisationRows:any[] = []
    columns:Column[] = []

    constructor(private organisationService: OrganisationService, private userService:UserService,
        private router: Router, private dialog: MatDialog, private functionService:FunctionService, public app:AppComponent) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.reloadOrganisations()
    }

    private reloadOrganisations() {
        if (!this.organisations) return
        const sysAdmin = this.isSysAdmin()

        this.organisationRows = this.organisations
            .map(o => ({
                ...o,
                isPartner: o.isPartner ? "Yes" : "No",
                isCustomer: o.notCustomer ? "No" : "Yes",
                consumption: o.isExemptFromPayment ? "No" : "Yes",
                canDelete: this.canDeleteOrganisation(o),
                canEdit: this.isOrgAdmin(o) && o.activated ? true : false,
                status: o.activated ? o.state : "Invited",
                invite: o.activated ? "" : ACCEPT_INVITE,
                roleName: this.getRoleName(o.role),
                colour: this.getRowColour(o),
            }))
            .sort((a,b) => (a.name || '').localeCompare(b.name || ''))

        const pendingInvites = this.organisationRows.some(r => r.invite)
        // system admins delete organisations; organisation admins close them (they end at the end of the month)
        const removeColumn = sysAdmin
            ? new Column('canDelete', 'Delete', 'icon-button', 'Delete the organisation permanently', 'warn', 'delete')
            : new Column('canDelete', 'Close', 'icon-button', 'Close the organisation at the end of the month', 'warn', 'event_busy')

        this.columns = this.app.smallScreen
            ? [
                new Column('name', 'Name', 'string'),
                new Column('status', 'Status', 'string'),
            ]
            : [
                new Column('name', 'Name', 'string'),
                new Column('status', 'Status', 'string'),
                new Column('roleName', 'My role', 'string'),
                new Column('numUsers', 'Users', 'number'),
                new Column('numAdmins', 'Admins', 'number'),
                new Column('createdDate', 'Created', 'date'),
                new Column('endDate', 'Ends', 'date'),
            ]
        if (pendingInvites) this.columns.push(new Column('invite', 'Invite', 'button', 'Join this organisation', 'accent'))
        if (!this.app.smallScreen && sysAdmin) {
            this.columns.push(new Column('isCustomer', 'Customer', 'string'))
            this.columns.push(new Column('isPartner', 'Partner', 'string'))
            this.columns.push(new Column('consumption', 'Consumption pricing', 'string'))
        }
        if (this.organisationRows.some(r => r.canEdit)) this.columns.push(new Column('canEdit', 'Edit', 'icon-button', 'Update organisation details', 'accent', 'edit'))
        if (this.organisationRows.some(r => r.canDelete)) this.columns.push(removeColumn)
    }

    async handleRowEvent(rowEvent:{row:any, column?:Column}) {
        const org = rowEvent.row && this.organisations.find(o => o._key === rowEvent.row._key)
        if (!org) return
        if (!rowEvent.column) {
            if (org.activated) this.select(org)
            else this.acceptInvite(org)
        }else if (rowEvent.column.name === "canDelete") {
            this.deleteOrganisation(org)
        }else if (rowEvent.column.name === "canEdit" && this.isOrgAdmin(org) && org.activated) {
            this.openOrganisationDialog(org)
        }else if (rowEvent.column.name === "invite" && !org.activated) {
            this.acceptInvite(org)
        }
    }

    async deleteOrganisation(org:any) {
        if (!this.canDeleteOrganisation(org)) return
        if (this.isSysAdmin()) {
            const message = `Permanently delete the organisation ${org.name}, with all of its models and data? This can't be undone.`
            const dialogData = new ConfirmDialogModel("Delete organisation", [message], "delete", false, "Delete", "Cancel");

            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                maxWidth: "500px",
                data: dialogData
            });

            dialogRef.afterClosed().subscribe(async dialogResult => {
                if (dialogResult?.button === "Delete") {
                    await this.organisationService.deleteOrganisation(org)
                    this.changed.emit()
                }
            });
        }else if (!org.endDate) {
            // confirms, then reports through the organisation service's update event
            await this.organisationService.closeOrganisation(org)
        }
    }

    getTableHeight() {
        return Math.max(300, window.innerHeight - 300)
    }

    getRowColour(organisation) {
        if (organisation.state === ORGANISATION_STATE.EXPIRED)
            return "red"
        else if (organisation.state === ORGANISATION_STATE.NEAR_EXPIRY)
            return "orange"
        else return ""
    }

    isExpired(organisation) {
        return organisation.state === ORGANISATION_STATE.EXPIRED
    }

    select(organisation:any) {
        if (!this.isExpired(organisation) || this.userService.isSysAdmin()) {
            this.router.navigateByUrl(DEFAULT_URL(organisation._key))
        }else if (organisation.role === USER_ROLE.ADMIN) {
            this.functionService.executeFunction(FUNCTION.PAY_SUBSCRIPTION, organisation)
        }
    }

    isSysAdmin() {
        return this.userService.isSysAdmin()
    }

    canDeleteOrganisation(o:any) {
        if (this.isSysAdmin()) return true
        if (o.endDate) return false
        return (this.isOrgAdmin(o) && o.activated)?true:false
    }

    async acceptInvite(organisation:any) {
        await this.organisationService.acceptInvite(organisation)
        this.select(organisation)
    }

    getRoleName(role) {
        return getUserRoleName(role)
    }

    isOrgAdmin(org:Organisation) {
        return this.isSysAdmin() || org.role === USER_ROLE.ADMIN
    }

    private async openOrganisationDialog(org:Organisation) {
        const ref = this.organisationService.openOrganisationDialog(org)

        ref.afterClosed().subscribe(async result => {
            if (result) {
                await this.organisationService.saveOrganisation(result, false)
                this.changed.emit()
            }
        })
    }
}
